import { apiClient } from "../../api/client";
import { AxiosProgressEvent } from "axios";
import { acroUploadResponse } from "../../data/mockData";

export interface UploadResponse {
  fileId: string;
  message: string;
  content: string;
  fileName: string;
  title: string;
  pdfType: "XFA_PDF" | "ACROFORM_PDF" | "DOCX";
}

export type UploadProgressHandler = (percent: number) => void;

export interface UploadOptions {
  onProgress?: UploadProgressHandler;
  email?: string;
}

export async function uploadFile(
  formData: FormData,
  options?: UploadOptions
): Promise<UploadResponse> {
  // Ensure the payload includes the current user's email under key 'userEmail'.
  // Prefer explicit option, fallback to localStorage where AuthProvider stores the current user.
  let email = options?.email?.trim();
  try {
    if (!email) {
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('currentUser') : null;
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed.email === 'string') {
          email = parsed.email;
        }
      }
    }

    if (email && !formData.get('userEmail')) {
      formData.append('userEmail', email);
    }
  } catch (err) {
    // Non-fatal: if we cannot determine email, continue without it but log for debugging.
    console.warn("Could not attach userEmail to upload payload", err);
  }

  // If client indicates ACRO upload, return dummy response instead of calling backend.
  const indicatedPdfType = (formData.get('pdfType') || undefined) as UploadResponse['pdfType'] | undefined;
  if (indicatedPdfType === 'ACROFORM_PDF') {
    // Build a new FormData with only the file for the ACRO endpoint
    try {
      const fileEntry = formData.get('file');
      if (!fileEntry) {
        console.error("ACRO upload: 'file' is missing in FormData");
      } else {
        const fd = new FormData();
        fd.append('use_cache', 'true');
        if (email) {
          fd.append('emailId', email);
        }
        if (fileEntry instanceof Blob) {
          const name = (fileEntry as File)?.name || 'upload.pdf';
          fd.append('file', fileEntry, name);
        } else {
          // If a string somehow, append as-is (server may reject)
          fd.append('file', fileEntry as unknown as string);
        }
        // Use separate base URL for ACRO intelligence endpoint
        const ACRO_INTELLIGENCE_URL = import.meta.env.VITE_PDF_INTELLIGENCE_URL || 'https://pdf-intelligence-kit.genesis.dev.platform.autonomize.ai';
        const acroResponse = await apiClient.post(
          `${ACRO_INTELLIGENCE_URL}/process-pdf`,
          fd,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const ratio =
                typeof progressEvent.progress === 'number'
                  ? progressEvent.progress
                  : progressEvent.total
                    ? (progressEvent.loaded ?? 0) / progressEvent.total
                    : undefined;
              if (typeof ratio === 'number' && isFinite(ratio)) {
                const percent = Math.min(Math.round(ratio * 100), 99);
                options?.onProgress?.(percent);
              }
            },
          }
        );
        console.log('ACRO upload response:', acroResponse?.data);
      }
    } catch (err) {
      console.error('ACRO upload error:', err);
    }

    // Simulate network latency if desired, but return immediately here.
    return { ...acroUploadResponse };
  } else if(indicatedPdfType == "DOCX") {
    const response = await apiClient.post<UploadResponse>(
    "/Docx/upload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        const ratio =
          typeof progressEvent.progress === "number"
            ? progressEvent.progress
            : progressEvent.total
              ? (progressEvent.loaded ?? 0) / progressEvent.total
              : undefined;
        if (typeof ratio === "number" && isFinite(ratio)) {
          const percent = Math.min(Math.round(ratio * 100), 99); // cap at 99%
          options?.onProgress?.(percent);
        }
      },
    });
    const apiResponse = response.data;
    const pdfType: UploadResponse['pdfType'] = indicatedPdfType ?? "DOCX";
    const finalObject: UploadResponse = {
      ...apiResponse,
      pdfType,
    };
    return finalObject; // Ensure data is returned correctly
  } else {
    const response = await apiClient.post<UploadResponse>(
      "/PDFConvertor/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const ratio =
            typeof progressEvent.progress === "number"
              ? progressEvent.progress
              : progressEvent.total
                ? (progressEvent.loaded ?? 0) / progressEvent.total
                : undefined;
          if (typeof ratio === "number" && isFinite(ratio)) {
            const percent = Math.min(Math.round(ratio * 100), 99); // cap at 99%
            options?.onProgress?.(percent);
          }
        },
      }
    );
    const apiResponse = response.data;
    const pdfType: UploadResponse['pdfType'] = indicatedPdfType ?? "XFA_PDF";
    const finalObject: UploadResponse = {
      ...apiResponse,
      pdfType,
    };
    return finalObject; // Ensure data is returned correctly
  }

}
